import React, { useEffect, useRef, useState } from "react";
import { Package, RefreshCw, Search as SearchIcon, ChevronUp, ChevronDown } from "lucide-react";
import baseapi from "../../../services/baseapi";
import { showError } from "../../../utils/toast";
import ApplicationVulnerabilitiesSidebar from "./ApplicationVulnerabilitiesSidebar";
import { Pagination } from "./Pagination";

// --- Helper Functions ---
function getTotalPages(total: number, per: number) {
    return Math.max(1, Math.ceil(total / per));
}

function formatDate(d: string) {
    return d?.length >= 10 ? d.slice(0, 10) : d || '';
}

const LoadingSpinner = ({ section }: { section: string }) =>
    <div className="flex flex-col items-center justify-center p-12 space-y-4">
        <div className="w-8 h-8 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Loading {section}...</p>
    </div>;

// --- SoftwareInfo Component ---
interface SoftwareInfoProps {
    agent_id: string;
    organization_id: string;
    asset_id: string;
    client_id: string;
    platform?: string;
}

interface Category {
    value: string;
    title: string;
}

const SOFTWARE_PER_PAGE_DEFAULT = 10;

const SoftwareInfo: React.FC<SoftwareInfoProps> = ({
    agent_id,
    organization_id,
    asset_id,
    client_id,
    platform,
}) => {
    const [categories, setCategories] = useState<Category[]>([]);
    const [activeCategory, setActiveCategory] = useState<string>("all");

    const [appsList, setAppsList] = useState<any[]>([]);
    const [totalApps, setTotalApps] = useState<number>(0);
    const [totalInstalled, setTotalInstalled] = useState<number>(0); // Store absolute total

    const [loadingCategories, setLoadingCategories] = useState(true);
    const [loadingApps, setLoadingApps] = useState(false);

    const [softwarePage, setSoftwarePage] = useState<number>(1);
    const [softwarePerPage, setSoftwarePerPage] = useState<number>(SOFTWARE_PER_PAGE_DEFAULT);

    const [search, setSearch] = useState<string>("");
    const [searchInput, setSearchInput] = useState<string>("");
    const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Refresh Key State
    const [refreshKey, setRefreshKey] = useState<number>(0);

    // Sorting State
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);

    // Sidebar State
    const [selectedAppId, setSelectedAppId] = useState<number | null>(null);
    const [selectedAppName, setSelectedAppName] = useState<string>("");
    const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

    const handleOpenVulnerabilities = (appId: number, appName: string) => {
        setSelectedAppId(appId);
        setSelectedAppName(appName);
        setIsSidebarOpen(true);
    };

    async function fetchCategories() {
        try {
            const params = { client_id, agent_id, asset_id };
            const response = await baseapi.get("/applications/categories", { params });
            if (response.data.status && response.data.data) {
                setCategories(response.data.data);
            }
        } catch (e: any) {
            showError(e.message || "Failed to load categories");
        } finally {
            setLoadingCategories(false);
        }
    }

    useEffect(() => {
        return () => {
            if (debounceTimer.current) clearTimeout(debounceTimer.current);
        };
    }, []);

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setSearchInput(value);

        if (debounceTimer.current) clearTimeout(debounceTimer.current);

        debounceTimer.current = setTimeout(() => {
            setSearch(value.trim());
            setSoftwarePage(1);
        }, 400);
    };

    // Fetch Categories First
    useEffect(() => {
        fetchCategories();
    }, [agent_id, asset_id, client_id]);

    // Fetch Applications
    useEffect(() => {
        async function fetchSoftware() {
            setLoadingApps(true);
            try {
                const skip = (softwarePage - 1) * softwarePerPage;
                const limit = softwarePerPage;

                const queryObj: any = {
                    client_id,
                    organization_id,
                    agent_id,
                    asset_id,
                };

                // Add Category Filter
                if (activeCategory !== "all") {
                    queryObj.category = activeCategory;
                }

                // Add Search Filter
                if (search.trim()) {
                    queryObj.$or = [
                        { app_name: { $ilike: `%${search.trim()}%` } },
                        { app_publisher: { $ilike: `%${search.trim()}%` } },
                        { app_version: { $ilike: `%${search.trim()}%` } },
                        { app_full_name: { $ilike: `%${search.trim()}%` } },
                        { uninstall_string: { $ilike: `%${search.trim()}%` } },
                    ];
                }

                const params: any = {
                    skip,
                    limit,
                    query: JSON.stringify(queryObj),
                };

                // Add API Sorting if requested
                if (sortConfig) {
                    params.sort_by = sortConfig.key;
                    params.sort_order = sortConfig.direction;
                }

                const response = await baseapi.get("/applications/with_vulnerabilities", { params });

                if (response.data.status) {
                    setAppsList(response.data.data || []);
                    setTotalApps(response.data.total || 0);

                    // If we are looking at 'all' without search, use this as our absolute total for the header
                    if (activeCategory === "all" && !search.trim()) {
                        setTotalInstalled(response.data.total || 0);
                    }
                }
            } catch (e: any) {
                showError(e.message || "Failed to load applications");
                setAppsList([]);
                setTotalApps(0);
            } finally {
                setLoadingApps(false);
            }
        }

        // Ensure we don't fetch before we have the base IDs
        if (asset_id) {
            fetchSoftware();
        }
    }, [agent_id, asset_id, organization_id, client_id, activeCategory, softwarePage, softwarePerPage, search, refreshKey, sortConfig]);

    // Handlers
    const handleCategoryClick = (categoryVal: string) => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        setActiveCategory(categoryVal);
        setSoftwarePage(1);
        setSearch("");
        setSearchInput("");
    };

    // const handleSearchSubmit = (e: React.FormEvent) => {
    //     e.preventDefault();
    //     setSearch(searchInput.trim());
    //     setSoftwarePage(1);
    // };

    const handleRefresh = () => {
        if (debounceTimer.current) clearTimeout(debounceTimer.current);
        setSearch("");
        setSearchInput("");
        setActiveCategory("all");
        setSoftwarePage(1);
        setSoftwarePerPage(SOFTWARE_PER_PAGE_DEFAULT);
        setSortConfig(null);
        fetchCategories();
        setRefreshKey(prev => prev + 1);
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
            }
            return { key, direction: "asc" };
        });
        setSoftwarePage(1); // Reset to page 1 on sort
    };

    // Helper for rendering sorting headers
    const SortableHeader = ({ label, sortKey, hide }: { label: string; sortKey: string; hide?: boolean }) => {
        const isActive = sortConfig?.key === sortKey;
        if (hide) return null;
        return (
            <th
                onClick={() => handleSort(sortKey)}
                className="px-3 py-2 text-left text-[10px] font-bold text-gray-200 uppercase tracking-wider border-b border-gray-700 cursor-pointer group select-none whitespace-nowrap"
            >
                <div className="flex items-center gap-1">
                    {label}
                    <div className="flex items-center">
                        {isActive ? (
                            sortConfig.direction === "asc" ? (
                                <ChevronUp size={12} className="text-orange-500" />
                            ) : (
                                <ChevronDown size={12} className="text-orange-500" />
                            )
                        ) : (
                            <ChevronDown size={12} className="text-gray-200" />
                        )}
                    </div>
                </div>
            </th>
        );
    };

    const isWindows = platform?.toLowerCase().includes("win") || platform?.toLowerCase() === "windows";

    if (loadingCategories) {
        return <LoadingSpinner section="categories" />;
    }

    return (
        <div className="p-3">
            <div className="flex items-center justify-between mb-3 border-b border-gray-700 pb-2">
                <div className="flex items-center">
                    <div className="p-1.5 bg-orange-600 rounded mr-2.5">
                        <Package className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col">
                        <h2 className="text-sm font-bold text-white leading-tight">Software Information</h2>
                        <p className="text-gray-200 text-[10px] uppercase font-semibold tracking-wider">
                            {totalInstalled > 0 ? `${totalInstalled} applications installed` : "Checking installed apps..."}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <button
                        onClick={handleRefresh}
                        disabled={loadingCategories || loadingApps}
                        className="p-2 bg-gray-800 hover:bg-gray-700 text-gray-300 hover:text-white border border-gray-700 rounded-md transition-colors shadow-sm flex items-center justify-center disabled:opacity-50 h-8 cursor-pointer"
                        title="Refresh data"
                    >
                        <RefreshCw className={`w-4 h-4 ${loadingCategories || loadingApps ? 'animate-spin text-orange-500' : ''}`} />
                    </button>

                    <div className="relative">
                        <SearchIcon className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                        <input
                            value={searchInput}
                            onChange={handleSearchChange}
                            placeholder="Search software..."
                            className="w-64 bg-gray-900 border border-gray-700 rounded-md pl-8 pr-7 py-1.5 text-xs text-white placeholder-gray-500 focus:outline-none focus:border-orange-500   transition-colors h-8"
                        />
                    </div>
                </div>
            </div>

            {/* Category Tabs */}
            <div className="flex flex-wrap gap-1 mb-3">
                {categories.map((category) => (
                    <button
                        key={category.value}
                        onClick={() => handleCategoryClick(category.value)}
                        className={`px-3 py-1.5 text-[11px] font-bold uppercase tracking-tight rounded-t-md border-b-2 transition-all duration-200 flex items-center ${activeCategory === category.value
                            ? 'text-white border-blue-500 bg-gray-800'
                            : 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-800/50'
                            }`}
                    >
                        {category.title}
                        {activeCategory === category.value && (
                            <span className="ml-1.5 opacity-80 text-blue-400 font-mono">({totalApps})</span>
                        )}
                    </button>
                ))}
            </div>

            {/* Table Area Container */}
            <div className="bg-gray-900/40 rounded-md border border-gray-700 overflow-hidden flex flex-col relative min-h-[300px]">

                {/* Scrollable Table Wrapper */}
                <div className="flex-1 overflow-x-auto overflow-y-auto custom-scroll">
                    <table className="w-full min-w-max text-left border-collapse">
                        <thead className="bg-gray-800/80 sticky top-0 z-10">
                            <tr>
                                <SortableHeader label="Application" sortKey="app_name" />
                                <SortableHeader label="Version" sortKey="app_version" />
                                <SortableHeader label="Publisher" sortKey="app_publisher" />
                                <SortableHeader label="Total CVEs" sortKey="total_cves" />
                                <SortableHeader label="Critical" sortKey="critical_count" />
                                <SortableHeader label="High" sortKey="high_count" />
                                <SortableHeader label="Medium" sortKey="medium_count" />
                                <SortableHeader label="Low" sortKey="low_count" />
                                <SortableHeader label="Highest Severity" sortKey="highest_severity" />
                                <SortableHeader label="Max CVSS" sortKey="max_cvss_score" />
                                <SortableHeader label="Install Date" sortKey="install_date" />
                                {/* <SortableHeader label="Size" sortKey="size_mb" hide={isWindows} /> */}
                                <SortableHeader label="Install Path" sortKey="install_location" />
                                <SortableHeader label="Uninstall String" sortKey="uninstall_string" hide={!isWindows} />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-800">
                            {loadingApps ? (
                                <tr>
                                    <td colSpan={13} className="h-48">
                                        <div className="flex flex-col items-center justify-center space-y-3 h-full">
                                            <div className="w-6 h-6 border-2 border-blue-500/20 border-t-blue-500 rounded-full animate-spin"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : appsList.length === 0 ? (
                                <tr>
                                    <td colSpan={13} className="h-48">
                                        <div className="flex flex-col items-center justify-center space-y-2 opacity-50 h-full">
                                            <Package size={32} className="text-gray-500 mb-2" />
                                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-widest">No applications found</p>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                appsList.map((app: any, idx: number) => (
                                    <tr key={idx} className="hover:bg-gray-800/40 transition-colors duration-100">
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            <div className="text-[11px] font-semibold text-white">{app.app_name || app.name || '-'}</div>
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap"><span className="text-[10px] text-gray-300 font-mono">{app.app_version || app.version || '-'}</span></td>
                                        <td className="px-3 py-2 whitespace-nowrap"><span className="text-[11px] text-gray-300 block">{app.app_publisher || app.publisher || 'Unknown'}</span></td>

                                        {/* Total CVEs */}
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {app.total_cves > 0 ? (
                                                <button
                                                    onClick={() => handleOpenVulnerabilities(app.id, app.app_name || app.name)}
                                                    className="font-mono font-bold text-[11px] text-red-400 hover:text-red-300 bg-red-500/10 hover:bg-red-500/20 px-2.5 py-0.5 rounded border border-red-500/20 transition-all cursor-pointer flex items-center gap-1"
                                                >
                                                    {app.total_cves}
                                                </button>
                                            ) : (
                                                <span className="font-mono text-[11px] text-gray-400 px-2 py-0.5">0</span>
                                            )}
                                        </td>

                                        {/* Severity Counts */}
                                        <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-red-400 font-bold">{app.critical_count ?? 0}</td>
                                        <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-orange-400 font-bold">{app.high_count ?? 0}</td>
                                        <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-yellow-400 font-bold">{app.medium_count ?? 0}</td>
                                        <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-green-400">{app.low_count ?? 0}</td>

                                        {/* Highest Severity */}
                                        <td className="px-3 py-2 whitespace-nowrap">
                                            {app.highest_severity ? (
                                                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-bold tracking-wider border ${app.highest_severity.toLowerCase().includes("critical")
                                                        ? "bg-red-500/10 text-red-400 border-red-500/30"
                                                        : app.highest_severity.toLowerCase().includes("high")
                                                            ? "bg-orange-500/10 text-orange-400 border-orange-500/30"
                                                            : app.highest_severity.toLowerCase().includes("medium")
                                                                ? "bg-yellow-500/10 text-yellow-400 border-yellow-500/30"
                                                                : "bg-green-500/10 text-green-400 border-green-500/30"
                                                    }`}>
                                                    {app.highest_severity}
                                                </span>
                                            ) : (
                                                <span className="text-gray-500">-</span>
                                            )}
                                        </td>

                                        {/* Max CVSS Score */}
                                        <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-orange-400 font-bold">
                                            {app.max_cvss_score !== undefined && app.max_cvss_score !== null ? app.max_cvss_score.toFixed(1) : "-"}
                                        </td>

                                        <td className="px-3 py-2 whitespace-nowrap"><span className="text-[10px] text-gray-400">{app.install_date === 'Unknown' ? '-' : formatDate(app.install_date)}</span></td>
                                        {/* {!isWindows && (
                                            <td className="px-3 py-2 whitespace-nowrap"><span className="text-[10px] text-gray-300 font-mono">{app.size_mb === "Unknown" ? "-" : `${app.size_mb} MB`}</span></td>
                                        )} */}
                                        <td className="px-3 py-2 whitespace-nowrap"><div className="text-[10px] text-gray-400">{app.install_location || '-'}</div></td>
                                        {isWindows && (
                                            <td className="px-3 py-2 whitespace-normal min-w-[220px]">
                                                <div className="text-[10px] text-gray-300 font-mono break-all select-all">
                                                    {app.uninstall_string || app.uninstallString || '-'}
                                                </div>
                                            </td>
                                        )}
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination Footer */}
                <div className="mt-auto border-t border-gray-700 shrink-0">
                    <Pagination
                        currentPage={softwarePage}
                        totalPages={getTotalPages(totalApps, softwarePerPage)}
                        onPageChange={setSoftwarePage}
                        itemsPerPage={softwarePerPage}
                        totalItems={totalApps}
                        onItemsPerPageChange={(val) => {
                            setSoftwarePerPage(val);
                            setSoftwarePage(1);
                        }}
                        itemsPerPageOptions={[10, 25, 50, 100]}
                    />
                </div>
            </div>

            {/* Sidebar */}
            <ApplicationVulnerabilitiesSidebar
                isOpen={isSidebarOpen}
                onClose={() => setIsSidebarOpen(false)}
                applicationId={selectedAppId}
                appName={selectedAppName}
            />
        </div>
    );
};

export default SoftwareInfo;